import { useQuery } from "@tanstack/react-query";
import { axios } from "./axios";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ROUTE } from "../routes";
import { env } from "../utils/env";

const fetchUserRoleAssignments = (authHeader: string, userId: number) => () =>
  axios.get(`${env.verifyServiceUrl}user/user_role/`, {
    headers: {
      Authorization: authHeader,
    },
    params: { user: userId },
  });

/**
 * A user's own Access Role assignment(s) — GET `/user/user_role/?user=`, the
 * reverse direction of `useUserRoles.ts` (which filters by `?role=` for a
 * Role's "Assigned users" tab). Backs the "Role" tab on `AddEditUserModal`.
 *
 * The `?user=` filter's response shape isn't confirmed — by symmetry with
 * `UserRoleAssignment` (confirmed only for `?role=`, where `role` comes back
 * as a bare id and `user` as a nested object), `role` may come back nested
 * here instead. Read either shape at the call site rather than assuming one.
 */
export const useUserRoleAssignments = (userId?: number) => {
  const { authHeader } = useAuth();
  const navigate = useNavigate();

  return useQuery(
    ["userRoleAssignments", userId ?? null],
    fetchUserRoleAssignments(authHeader, userId as number),
    {
      enabled: typeof userId === "number",
      retry: false,
      onError: (err: any) => {
        if (err.request?.status === 500 || err.request?.status === 503) {
          navigate(ROUTE.SERVER_ERROR);
        }
        if (err.request?.status === 401) {
          navigate(ROUTE.LOGOUT);
        }
      },
    }
  );
};
